import React, { useState } from 'react';
import { Routes, Route, Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    LayoutDashboard, MessageSquare, Settings, LogOut,
    Briefcase, UserCheck, DollarSign, Menu, X as CloseIcon,
    ArrowLeft
} from 'lucide-react';
import { ToastContainer } from './Toast';
import DashboardOverview from './DashboardOverview';
import InquiriesPage from './InquiriesPage';
import PortfolioPage from './PortfolioPage';
import CareersPage from './CareersPage';
import FinancePage from './FinancePage';
import SettingsPage from './SettingsPage';
import API_URL from '../../config/api';

const AdminDashboard = () => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [toasts, setToasts] = useState([]);
    const location = useLocation();
    const navigate = useNavigate();

    const showToast = (message, type = 'success', duration = 3000, actions = null) => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type, duration, actions }]);
        return id;
    };

    const removeToast = (id) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    };

    const handleLogout = () => {
        const id = showToast(
            'Are you sure you want to logout?',
            'warning',
            0,
            {
                cancelText: 'Cancel',
                confirmText: 'Logout',
                onCancel: () => removeToast(id),
                onConfirm: () => {
                    removeToast(id);
                    // Handle API logout if token exists
                    const token = localStorage.getItem('admin_token');
                    if (token) {
                        fetch(`${API_URL}/admin/logout`, {
                            method: 'POST',
                            headers: {
                                'Authorization': `Bearer ${token}`,
                                'Accept': 'application/json'
                            }
                        }).catch(console.error);
                    }
                    localStorage.removeItem('admin_token');
                    localStorage.removeItem('isAdminAuthenticated');
                    navigate('/admin');
                    showToast('Logged out successfully', 'success');
                }
            }
        );
    };

    const navItems = [
        { label: 'Dashboard', path: '/admin/dashboard', icon: LayoutDashboard },
        { label: 'Inquiries', path: '/admin/inquiries', icon: MessageSquare },
        { label: 'Portfolio', path: '/admin/portfolio', icon: Briefcase },
        { label: 'Careers', path: '/admin/careers', icon: UserCheck },
        { label: 'Finance', path: '/admin/finance', icon: DollarSign },
        { label: 'Settings', path: '/admin/settings', icon: Settings },
    ];

    return (
        <div className="min-h-screen bg-slate-50 flex text-dark-text relative">
            <ToastContainer toasts={toasts} removeToast={removeToast} />

            {/* Mobile Overlay */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/50 backdrop-blur-sm z-10 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside className={`w-64 border-r border-slate-200 bg-white flex flex-col fixed inset-y-0 z-20 transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
                <div className="p-8 pb-4">
                    <div className="flex items-center space-x-3 mb-10">
                        <div className="w-16 h-16 rounded-lg flex items-center justify-center">
                            <img src="/images/Codebytz.png" alt="Codebytz Logo" className="w-16 h-16 object-contain"/>
                        </div>
                        <span className="text-2xl font-bold tracking-tighter text-dark-text">
                            CODE<span className="text-brand-teal">BYTZ</span>
                        </span>
                    </div>

                    <nav className="space-y-2">
                        {navItems.map((item) => {
                            const isActive = location.pathname.startsWith(item.path);
                            return (
                                <Link
                                    key={item.label}
                                    to={item.path}
                                    onClick={() => setSidebarOpen(false)}
                                    className={`w-full flex items-center space-x-3 px-4 py-2.5 rounded-xl transition-all ${
                                        isActive
                                            ? 'bg-brand-teal text-white font-bold shadow-lg shadow-brand-teal/10'
                                            : 'text-gray-800 font-semibold hover:bg-slate-50 hover:text-dark-text'
                                    }`}
                                >
                                    <item.icon className="w-5 h-5" />
                                    <span>{item.label}</span>
                                </Link>
                            );
                        })}
                    </nav>
                </div>

                <div className="mt-auto p-8 pt-4 border-t border-slate-100">
                    <Link
                        to="/"
                        className="flex items-center space-x-3 px-4 py-3 rounded-xl text-slate-500 hover:bg-slate-50 hover:text-dark-text font-medium transition-all w-full mb-2"
                    >
                        <ArrowLeft className="w-5 h-5" />
                        <span>Back to Site</span>
                    </Link>
                    <button
                        onClick={handleLogout}
                        className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-red-500 hover:bg-red-500/10 transition-all font-medium"
                    >
                        <LogOut className="w-5 h-5" />
                        <span>Logout</span>
                    </button>
                </div>
            </aside>

            {/* Main Content */}
            <main className="flex-1 lg:ml-64 flex flex-col min-h-screen relative overflow-x-hidden">
                <header className="h-20 bg-white/80 backdrop-blur-md border-b border-slate-200 flex items-center justify-between px-4 sm:px-8 sticky top-0 z-10">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => setSidebarOpen(!sidebarOpen)}
                            className="lg:hidden p-2 text-slate-500 hover:bg-slate-100 rounded-lg transition-colors"
                        >
                            {sidebarOpen ? <CloseIcon className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                        </button>
                        <div className="text-sm font-medium text-slate-500 lowercase hidden sm:block">
                            <span className="text-dark-text capitalize">Admin</span> / {location.pathname.split('/').pop() || 'dashboard'}
                        </div>
                    </div>

                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-3">
                            <div className="text-right hidden sm:block">
                                <p className="text-sm font-bold text-dark-text">Admin</p>
                                <p className="text-xs text-slate-500">Administrator</p>
                            </div>
                            <div className="w-10 h-10 rounded-full bg-brand-teal/10 flex items-center justify-center text-brand-teal font-bold border border-brand-teal/20">
                                AD
                            </div>
                        </div>
                    </div>
                </header>

                <div className="p-4 sm:p-8 flex-1 w-full max-w-7xl mx-auto">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={location.pathname}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.2 }}
                            className="w-full"
                        >
                            <Routes>
                                <Route path="/" element={<Navigate to="dashboard" replace />} />
                                <Route path="dashboard" element={<DashboardOverview showToast={showToast} />} />
                                <Route path="inquiries" element={<InquiriesPage showToast={showToast} />} />
                                <Route path="portfolio" element={<PortfolioPage showToast={showToast} />} />
                                <Route path="careers" element={<CareersPage showToast={showToast} />} />
                                <Route path="finance" element={<FinancePage showToast={showToast} />} />
                                <Route path="settings" element={<SettingsPage showToast={showToast} />} />
                                <Route path="*" element={<Navigate to="dashboard" replace />} />
                            </Routes>
                        </motion.div>
                    </AnimatePresence>
                </div>
            </main>
        </div>
    );
};

export default AdminDashboard;
